import { array, option } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { useMemo } from 'react';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { CountryCode, FeatureFlag } from '@mews-commander/enums';
import {
    AccountingEnvironment,
    ApplicableBillTypeParameters,
    Counter,
    EnterpriseScopeCounterDiscriminator,
    useRepositoryQuery,
} from '@mews-commander/data-access';
import { useFeatureFlag } from '@mews/framework';
import { getCurrentEnterpriseCountryCode } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { getAccountingEnvironmentByCode } from '@mews-commander/legacy-entities/src/ducks/accountingEnvironments';
import { useCommanderSelector } from '@mews-commander/utils';
import { CounterUseFor } from './components/CounterForm/formMapping';

export const useCanAddOrResetCounters = () => {
    const countryCode: CountryCode = useCommanderSelector(getCurrentEnterpriseCountryCode);

    const disallowedCountries = [CountryCode.France];

    return !disallowedCountries.includes(countryCode);
};

export const convertUseForFormValueToApplicableBillType = (useFor: CounterUseFor[]): ApplicableBillTypeParameters => ({
    applyToBill: useFor.includes(CounterUseFor.bills),
    applyToInvoice: useFor.includes(CounterUseFor.invoices),
});

export const useParseNumberFormatFromString = (numberFormat: string | undefined) =>
    useMemo<{ numberOfDigits: number; prefix: string | null; suffix: string | null } | null>(() => {
        if (!numberFormat) {
            return {
                numberOfDigits: 1,
                prefix: null,
                suffix: null,
            };
        }

        const matchResult = /(?<prefix>.+)?\{0:(?<numberOfDigits>0+)}(?<suffix>.+)?/.exec(numberFormat);

        if (!matchResult || !matchResult.groups) {
            return {
                numberOfDigits: 1,
                prefix: numberFormat,
                suffix: null,
            };
        }

        return {
            numberOfDigits: matchResult.groups.numberOfDigits?.length || 1,
            prefix: matchResult.groups.prefix || null,
            suffix: matchResult.groups.suffix || null,
        };
    }, [numberFormat]);

export const useGetCreditNoteCounters = () => {
    const { data } = useRepositoryQuery('counters', 'getAll', {});

    return pipe(
        data?.counters,
        option.fromNullable,
        option.map(
            array.filter((it) => it.data.discriminator === EnterpriseScopeCounterDiscriminator.CreditNoteBillCounter)
        ),
        option.fold(
            () => [],
            (items) => items
        )
    );
};

export const useIsCreditNoteCountersEnabled = () => {
    const { accountingEnvironmentCode } = useCurrentEnterprise();
    const environment = useCommanderSelector((state) =>
        getAccountingEnvironmentByCode(state, {
            code: accountingEnvironmentCode,
        })
    ) as AccountingEnvironment;

    const creditNoteCountersEnabled = useFeatureFlag(FeatureFlag.creditNoteCounterForItalyBasqueSpain);

    return !!environment.isCreditNoteBillCounterRequired && creditNoteCountersEnabled;
};

export const getNumberFormatFromCreditNoteCounter = (data: Counter['data']) => {
    if (data.discriminator !== EnterpriseScopeCounterDiscriminator.CreditNoteBillCounter) return;

    return `${data.value.prefix || ''}${array.makeBy(data.value.numberOfDigits, (_) => 0).join('')}`;
};
